import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body) {
  if (!body?.success) {
    throw new Error(sanitizeBusinessMessage(body?.message, 'Request failed'))
  }
  return body.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type ProductsListParams = {
  page?: number
  limit?: number
  q?: string
  isActive?: boolean
  categorySlug?: string
  lowStock?: boolean
  storeId?: number
}

export type ProductsListResponse = {
  items: unknown[]
  total: number
  page: number
  limit: number
}

/**
 * Authenticated catalog CRUD — matches shopora_Api `GET|POST|PATCH|DELETE /products`.
 */
export async function fetchProducts(params: ProductsListParams = {}): Promise<ProductsListResponse> {
  try {
    const page = params.page ?? 1
    const limit = params.limit ?? 20
    const query: Record<string, string | number | boolean> = { page, limit }
    if (params.q != null && params.q !== '') query.q = params.q
    if (params.isActive !== undefined) query.isActive = params.isActive
    if (params.categorySlug != null && params.categorySlug !== '')
      query.categorySlug = params.categorySlug
    if (params.lowStock) query.lowStock = true
    if (params.storeId != null) query.storeId = params.storeId

    const { data } = await api.get('/products', { params: query })
    const payload = assertSuccess(data) as ProductsListResponse
    return {
      items: payload.items || [],
      total: payload.total ?? 0,
      page: payload.page ?? page,
      limit: payload.limit ?? limit,
    }
  } catch (e) {
    throw apiError(e, 'Failed to load products')
  }
}

export async function fetchProductById(id) {
  try {
    const { data } = await api.get(`/products/${encodeURIComponent(id)}`)
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to load product')
  }
}

export async function createProduct(payload) {
  try {
    const { data } = await api.post('/products', payload)
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to create product')
  }
}

export async function updateProduct(id, payload) {
  try {
    const { data } = await api.patch(`/products/${encodeURIComponent(id)}`, payload)
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to update product')
  }
}

export type BatchUpdateProductsPayload = {
  productIds: number[]
  price?: number
  stockQuantity?: number
}

export type BatchUpdateProductsFailure = {
  id: number
  message: string
  code?: string
}

export type BatchUpdateProductsResponse = {
  succeeded: number
  failed: BatchUpdateProductsFailure[]
}

export async function batchUpdateProducts(
  payload: BatchUpdateProductsPayload,
): Promise<BatchUpdateProductsResponse> {
  try {
    const { data } = await api.patch('/products/batch', payload)
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to update products')
  }
}

export async function deleteProduct(id) {
  try {
    const { data } = await api.delete(`/products/${encodeURIComponent(id)}`)
    assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to delete product')
  }
}

export type ProductReviewItem = {
  id: number
  rating: number
  title: string | null
  body: string | null
  authorName: string
  isHidden: boolean
  createdAt: string
}

export type ProductReviewsResponse = {
  items: ProductReviewItem[]
  count: number
  averageRating: number | null
}

export async function fetchProductReviews(productId: string | number): Promise<ProductReviewsResponse> {
  try {
    const { data } = await api.get(`/products/${encodeURIComponent(productId)}/reviews`)
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to load reviews')
  }
}

export async function setProductReviewHidden(
  productId: string | number,
  reviewId: number,
  hidden: boolean,
): Promise<ProductReviewItem> {
  try {
    const { data } = await api.patch(
      `/products/${encodeURIComponent(productId)}/reviews/${encodeURIComponent(reviewId)}`,
      { hidden },
    )
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, hidden ? 'Failed to hide review' : 'Failed to show review')
  }
}

export type ProductMetricsTrendBucket = {
  date: string
  label: string
  unitsSold: number
  revenue: number
}

export type ProductMetricsTrend = {
  granularity: 'day'
  days: number
  buckets: ProductMetricsTrendBucket[]
}

export type ProductMetricsResponse = {
  unitsSold: number
  revenue: number
  currency: string
  lastSaleAt: string | null
  source: string
  trend: ProductMetricsTrend
  message?: string
}

export async function fetchProductMetrics(productId: string | number): Promise<ProductMetricsResponse> {
  try {
    const { data } = await api.get(`/products/${encodeURIComponent(productId)}/metrics`)
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to load metrics')
  }
}
